// File: src/services/offlineStorage.js

const DB_NAME = "AtomicOfflineAudio";
const DB_VERSION = 1;
const STORE_NAME = "encryptedTracks";

/**
 * Initializes IndexedDB storage
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "songId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(e.target.error);
  });
};

/**
 * Generates an AES-GCM encryption key using Web Crypto API
 */
const generateEncryptionKey = async () => {
  return await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
};

/**
 * Encrypts and saves an audio file locally for offline streaming
 */
export const saveEncryptedTrack = async (songId, audioUrl) => {
  try {
    // 1. Fetch raw audio binary
    const response = await fetch(audioUrl);
    const arrayBuffer = await response.arrayBuffer();

    // 2. Encrypt binary
    const key = await generateEncryptionKey();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // Initialization Vector

    const encryptedContent = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      arrayBuffer
    );

    // Export key to raw format for storing
    const exportedKey = await crypto.subtle.exportKey("raw", key);

    // 3. Store in IndexedDB
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.put({
        songId,
        encryptedContent,
        iv: Array.from(iv),
        key: Array.from(new Uint8Array(exportedKey)),
        savedAt: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    return { success: true };
  } catch (error) {
    console.error("Failed to encrypt and store audio locally:", error);
    return { success: false, error };
  }
};

/**
 * Retrieves and decrypts a locally stored track for offline playback
 */
export const getDecryptedTrackBlob = async (songId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);

    const record = await new Promise((resolve, reject) => {
      const request = store.get(songId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!record) return null;

    // Reconstruct IV and Key
    const iv = new Uint8Array(record.iv);
    const keyBuffer = new Uint8Array(record.key).buffer;

    const importedKey = await crypto.subtle.importKey(
      "raw",
      keyBuffer,
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    // Decrypt content
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      importedKey,
      record.encryptedContent
    );

    // Return playable Blob Object URL
    const blob = new Blob([decryptedBuffer], { type: "audio/mpeg" });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error decrypting local track:", error);
    return null;
  }
};